import * as React from "react";
import {cn} from "@/lib/utils";
import {Label} from "@/components/ui/label.tsx";
import {ImageConfig} from "@/constants";
import {motion} from "framer-motion";
import {LockIcon} from "lucide-react";
import { Button } from "./button";

interface PasswordInputProps extends Omit<React.ComponentProps<"input">, 'onChange'> {
    fullWidth?: boolean;
    label?: string;
    hasError?: boolean;
    errorMessage?: string;
    onChange: (value: string) => void;
    icon?: React.ReactNode;
}

function PasswordInput(props: PasswordInputProps) {
    const { onChange, hasError, errorMessage, className, fullWidth, label, id, name, ...rest} = props;
    const [isVisible, setIsVisible] = React.useState(false);
    
   
    return (
        <div className="mb-7">
            {label && <Label htmlFor={label} className="mt-3 mb-3 font-medium text-[16px]">{label}</Label>}
            <div className="relative flex items-center">
                <div className={cn("relative", fullWidth ? "w-full" : "")}>
                    <div
                        className="absolute inset-y-0 start-0 flex items-center ps-4 pointer-events-none peer-focus:text-blue-500">
                        <LockIcon className="size-4 text-gray-500 peer-focus:text-blue-500"/>
                    </div>
                    {/* Input Field */}
                    <input
                        {...rest}
                        id={id}
                        name={name}
                        type={isVisible ? "text" : "password"}
                        data-slot="input"
                        aria-label={label || "Password"}
                        aria-invalid={hasError ? "true" : "false"}
                        aria-describedby={hasError && errorMessage ? id : undefined}
                        className={cn(
                            "peer border-input file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-12 min-w-0 rounded-md border bg-[var(--input-background)] px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-base",
                            "pr-16 ps-11" ,
                            fullWidth ? "w-full" : "",
                            hasError
                                ? "border-red-500 focus:border-red-500"
                                : "focus:border-transparent focus:[var(--input-highlight-border)] focus:ring-[var(--input-highlight-border)] focus:ring-[0.25px] ",
                            className
                        )}
                        onChange={(event) => onChange(event.target.value)}
                    />

                    {/* Right Icons Container */}
                    <div className="absolute inset-y-0 end-0 flex items-center pe-3 gap-2">
                        {/* Toggle Password Visibility Icon with animation */}
                        <Button
                            onClick={() => setIsVisible(!isVisible)}
                            variant={"icon"}
                            className="p-0"
                            name="toggle-password-visibility"
                            aria-label={isVisible ? "Hide password" : "Show password"}
                            type="button"
                        >
                            <motion.span
                                initial={{rotate: 0, opacity: 0.8}}
                                animate={{rotate: isVisible ? 180 : 0, opacity: 1}}
                                transition={{duration: 0.2, ease: "easeInOut"}}
                                aria-hidden="true"
                            >
                                {isVisible ? <ImageConfig.EyeClosed/> : <ImageConfig.EyeOpen/>}
                            </motion.span>
                        </Button>

                        {/* Error Icon */}
                        {hasError && (
                            <svg
                                className="shrink-0 size-4 text-red-500"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" x2="12" y1="8" y2="12"></line>
                                <line x1="12" x2="12.01" y1="16" y2="16"></line>
                            </svg>
                        )}
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {hasError && errorMessage && (
                <p      
                    id={   id+"-error-message"}
                    className="text-[14px] text-sm text-red-500 pt-1 whitespace-normal font-normal tracking-[0.07px]"
                    role="alert"
                    aria-live="polite"
                >
                    {errorMessage}
                </p>
            )}
        </div>
    );
}

export {PasswordInput};
